// src/utils/dobDate.js
//
// Date-only (YYYY-MM-DD) <-> local Date adapters for date-of-birth fields.
//
// A DOB has no time or timezone. Converting through `toISOString()` shifts the
// day across midnight for anyone west of UTC or during BST, so both directions
// here use local calendar fields via date-fns and never touch UTC.
import { format, isValid, parse } from 'date-fns';

export const DOB_ISO_FORMAT = 'yyyy-MM-dd';
export const DOB_DISPLAY_FORMAT = 'dd/MM/yyyy';

const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse an ISO date-only string into a local-midnight Date.
 * Returns null for empty input and for anything that is not a real calendar
 * date (e.g. `2001-02-29`, `not-a-date`, a full ISO datetime).
 */
export const parseDob = (value) => {
    if (typeof value !== 'string' || !ISO_DATE_ONLY.test(value)) return null;
    const date = parse(value, DOB_ISO_FORMAT, new Date(2000, 0, 1));
    return isValid(date) && format(date, DOB_ISO_FORMAT) === value ? date : null;
};

/** Format a Date back to the ISO date-only booking payload value ('' when null/invalid). */
export const formatDob = (date) => (date instanceof Date && isValid(date) ? format(date, DOB_ISO_FORMAT) : '');

/** True when the value is a real ISO date-only string. */
export const isValidDobString = (value) => parseDob(value) !== null;

// Our API renders date-only fields with DRF's DATE_FORMAT, "%d-%m-%Y", so a
// date of birth arrives as "18-08-1995" — NOT ISO. Every dob field in the UI
// works in ISO ('YYYY-MM-DD'), so the two must be converted at the boundary.
// Returns '' for absent or unparseable input, and passes an already-ISO value
// straight through, so it is safe to call on either shape.
export const dobFromApi = (value) => {
    if (!value || typeof value !== 'string') return '';
    const dayFirst = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dayFirst) {
        const [, dd, mm, yyyy] = dayFirst;
        const iso = `${yyyy}-${mm}-${dd}`;
        return isValidDobString(iso) ? iso : '';
    }
    return isValidDobString(value) ? value : '';
};
